import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../modal/Modal';
import EmbryoDetailsTable, { hasDuplicateStrawNumbers, StrawRow } from '../embryo-table/EmbryoDetailsTable';
import FilterDropdown, { defaultFilterOption, FilterOption } from '../custom-input/FilterDropdown';
import SelectStorageLocations from '../select-storage-locations/SelectStorageLocations';
import { Loader, ValidatedInput } from '../';
import AlertSVG from '../svgs/Alert.svg';
import { AccountStrawState, AddStrawLevelDataModalMode } from '../../types/interfaces/addStrawLevelData.interfaces';
import { StorageLocation } from '../../types/interfaces/inventoryTransaction.interfaces';
import { saveExistingEmbryoStrawData } from '../../api/embryoStrawsApi';
import { ACCOUNT_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS, VALIDATION_ALERT_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { InfoSVG } from '../../components/svgs';
import {
  buildExistingEmbryoStrawSavePayload,
  buildStrawDataForCount,
  loadAccountStrawStatesForSpecimen,
  resequenceAccountStrawStates,
} from '../../services/strawLevelData.service';
import { showToast } from '../../services/toast.service';
import './addStrawLevelDataModal.scss';

type AddStrawLevelDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode: AddStrawLevelDataModalMode;
  specimenIds: number[];
  preselectedSpecimenId?: number;
};

const getTotalQuantity = (strawData: StrawRow[]) =>
  strawData.reduce((totalQuantity, strawRow) => totalQuantity + (Number(strawRow.embryoCountPerStraw) || 0), 0);

const buildStrawCodeOptions = (strawData: StrawRow[]): FilterOption<string>[] =>
  strawData
    .filter(strawRow => strawRow.embryoStrawNumber.trim())
    .map(strawRow => ({
      name: `${strawRow.embryoStrawNumber}${strawRow.embryoCountPerStraw ? ` (Qty: ${strawRow.embryoCountPerStraw})` : ''}`,
      value: strawRow.embryoStrawNumber,
      quantity: strawRow.embryoCountPerStraw,
    }));

const getAssignedStrawCodes = (storageLocations: StorageLocation[]) =>
  storageLocations.flatMap(location => (location.strawCodesText ?? '').split(',').filter(Boolean));

const toSpecimenOption = (specimenId: number): FilterOption<string> => ({
  name: String(specimenId),
  value: String(specimenId),
});

const getDefaultSpecimenId = (
  specimenIds: number[],
  mode: AddStrawLevelDataModalMode,
  preselectedSpecimenId?: number,
): number | undefined => {
  if (specimenIds.length === 0) return undefined;

  if (mode === AddStrawLevelDataModalMode.Specimen && preselectedSpecimenId !== undefined) {
    return specimenIds.includes(preselectedSpecimenId) ? preselectedSpecimenId : specimenIds[0];
  }

  return specimenIds[0];
};

const isStrawDataValid = (strawData: StrawRow[]) =>
  strawData.length > 0 &&
  strawData.every(strawRow => strawRow.embryoStrawNumber.trim().length > 0 && Number(strawRow.embryoCountPerStraw) > 0) &&
  !hasDuplicateStrawNumbers(strawData);

const AddStrawLevelDataModal: FC<AddStrawLevelDataModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  specimenIds,
  preselectedSpecimenId,
}) => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSpecimenOption, setSelectedSpecimenOption] = useState<FilterOption<string>>(defaultFilterOption);
  const [accountStrawStates, setAccountStrawStates] = useState<AccountStrawState[]>([]);
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
  const [isValidationAlertActive, setIsValidationAlertActive] = useState(false);

  const loadSpecimenAccountData = useCallback(async (specimenId: number) => {
    setLoading(true);
    try {
      const { accountStates, storageLocations: locations } = await loadAccountStrawStatesForSpecimen(specimenId);
      setAccountStrawStates(accountStates);
      setStorageLocations(locations);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setAccountStrawStates([]);
      setStorageLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetModalState = useCallback(() => {
    setSelectedSpecimenOption(defaultFilterOption);
    setAccountStrawStates([]);
    setStorageLocations([]);
    setIsValidationAlertActive(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
      return;
    }

    const initialSpecimenId = getDefaultSpecimenId(specimenIds, mode, preselectedSpecimenId);

    if (initialSpecimenId !== undefined) {
      setSelectedSpecimenOption(toSpecimenOption(initialSpecimenId));
      loadSpecimenAccountData(initialSpecimenId);
    } else {
      resetModalState();
    }
  }, [isOpen, specimenIds, mode, preselectedSpecimenId, loadSpecimenAccountData, resetModalState]);

  const handleSpecimenChange = (selectedOption: FilterOption<string>) => {
    setSelectedSpecimenOption(selectedOption);
    setIsValidationAlertActive(false);
    loadSpecimenAccountData(Number(selectedOption.value));
  };

  const handleNumStrawsChange = (accountId: number, numStrawsValue: number) => {
    const parsedNumStraws = Number.isNaN(numStrawsValue) ? 0 : Math.max(0, numStrawsValue);
    setIsValidationAlertActive(false);

    setAccountStrawStates(currentStates => {
      const updatedStates = currentStates.map(accountState => {
        const { accountId: stateAccountId, strawData } = accountState;

        if (stateAccountId !== accountId) return accountState;

        return {
          ...accountState,
          numStraws: parsedNumStraws,
          strawData: buildStrawDataForCount(parsedNumStraws, strawData),
        };
      });

      return resequenceAccountStrawStates(updatedStates);
    });
  };

  const handleStrawDataChange = (accountId: number, updatedStrawRows: StrawRow[]) => {
    setAccountStrawStates(currentStates => {
      const previousLength = currentStates.find(accountState => accountState.accountId === accountId)?.strawData.length ?? 0;

      const updatedStates = currentStates.map(accountState => {
        if (accountState.accountId !== accountId) return accountState;

        return { ...accountState, strawData: updatedStrawRows, numStraws: updatedStrawRows.length };
      });

      // Resequence only when rows are added/removed so Straw Number edits are preserved.
      return previousLength !== updatedStrawRows.length ? resequenceAccountStrawStates(updatedStates) : updatedStates;
    });
  };

  const allStrawCodeOptions = useMemo(
    () => accountStrawStates.flatMap(accountState => buildStrawCodeOptions(accountState.strawData)),
    [accountStrawStates],
  );

  const allStrawCodes = useMemo(
    () =>
      accountStrawStates.flatMap(accountState =>
        accountState.strawData.map(strawRow => strawRow.embryoStrawNumber).filter(Boolean),
      ),
    [accountStrawStates],
  );

  const accountForms = useMemo(() => {
    let globalIndex = 0;

    return accountStrawStates.map(accountState => {
      const startIndex = globalIndex;
      globalIndex += accountState.strawData.length;

      return {
        accountState,
        startIndex,
      };
    });
  }, [accountStrawStates]);

  const hasAnyStraws = allStrawCodes.length > 0;

  const totalQuantity = useMemo(
    () => accountStrawStates.reduce((sum, accountState) => sum + accountState.currentQuantity, 0),
    [accountStrawStates],
  );

  const isStorageLocationsValid = useCallback(() => {
    if (storageLocations.length === 0) return false;

    const assignedStrawCodes = getAssignedStrawCodes(storageLocations);

    if (assignedStrawCodes.length !== allStrawCodes.length) return false;
    if (new Set(assignedStrawCodes).size !== assignedStrawCodes.length) return false;
    if (!allStrawCodes.every(strawCode => assignedStrawCodes.includes(strawCode))) return false;

    return storageLocations.every(storageLocation => {
      const selectedStraws = (storageLocation.strawCodesText ?? '').split(',').filter(Boolean);
      if (selectedStraws.length === 0) return false;

      const totalStrawQuantity = selectedStraws.reduce((runningTotal, strawCode) => {
        const matchingStrawOption = allStrawCodeOptions.find(strawOption => strawOption.value === strawCode);
        return runningTotal + (matchingStrawOption?.quantity || 0);
      }, 0);

      return storageLocation.quantity.value === totalStrawQuantity;
    });
  }, [storageLocations, allStrawCodes, allStrawCodeOptions]);

  const isAccountStateValid = useCallback((accountState: AccountStrawState) => {
    if (accountState.currentQuantity === 0) return true;
    if (accountState.strawData.length === 0) return false;

    return isStrawDataValid(accountState.strawData) && getTotalQuantity(accountState.strawData) === accountState.currentQuantity;
  }, []);

  const isFormValid = useCallback(
    () =>
      hasAnyStraws && isStorageLocationsValid() && accountStrawStates.every(accountState => isAccountStateValid(accountState)),
    [hasAnyStraws, isStorageLocationsValid, accountStrawStates, isAccountStateValid],
  );

  const hasUnassignedStraws = useCallback(() => {
    const assignedStrawCodes = getAssignedStrawCodes(storageLocations);
    return allStrawCodes.some(strawCode => !assignedStrawCodes.includes(strawCode));
  }, [storageLocations, allStrawCodes]);

  const handleSave = async () => {
    if (accountStrawStates.length === 0) return;

    setIsValidationAlertActive(true);

    if (!isFormValid()) return;

    const savePayload = buildExistingEmbryoStrawSavePayload(accountStrawStates, storageLocations);

    if (!savePayload.length) return;

    const embryoStraws = savePayload[0].embryoStraws ?? [];
    const missingCanisterStraws = embryoStraws.filter(straw => !straw.storageCanisterId);

    if (missingCanisterStraws.length > 0) return;

    setSaving(true);
    try {
      await saveExistingEmbryoStrawData(savePayload);
      showToast.success(toastMessages.SUCCESS);
      onSuccess?.();
      onClose();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setSaving(false);
    }
  };

  const renderAccountForm = (accountState: AccountStrawState, globalStartIndex: number) => {
    const { accountId, accountName, currentQuantity, numStraws, strawData } = accountState;
    const strawTotal = getTotalQuantity(strawData);
    const hasStrawQuantityEntry = strawData.some(strawRow => Number(strawRow.embryoCountPerStraw) > 0);
    const isQuantityMismatch = hasStrawQuantityEntry && strawTotal !== currentQuantity;

    return (
      <div className="account-straw-section" key={`account-straw-${accountId}`}>
        <h5 className="section-heading">{`${ACCOUNT_CONSTANTS.ACCOUNT}: ${accountName}`}</h5>

        <div className="specimen-details">
          <div className="specimen-detail">
            <label>{LABEL_CONSTANTS.QUANTITY}:</label>
            <span>{currentQuantity}</span>
          </div>
        </div>

        <div className="specimen-field-row">
          <label htmlFor={`num-straws-${accountId}`}>{LABEL_CONSTANTS.NUMBER_OF_STRAWS}</label>
          <div className="input-container">
            <ValidatedInput
              key={`num-straws-input-${accountId}-${numStraws}`}
              label={<></>}
              type="number"
              placeholder="0"
              useId={`num-straws-${accountId}`}
              validatedStateForAutoFill={{
                value: numStraws ? String(numStraws) : '',
                valid: true,
              }}
              setValidatedState={validatedInputState => {
                const sanitizedValue = validatedInputState.value.replace(/\D/g, '');
                handleNumStrawsChange(accountId, sanitizedValue === '' ? 0 : Number(sanitizedValue));
              }}
              validators={[]}
            />
          </div>
        </div>

        {numStraws > 0 && (
          <EmbryoDetailsTable
            data={strawData}
            globalStartIndex={globalStartIndex}
            onChange={updatedStrawRows => handleStrawDataChange(accountId, updatedStrawRows)}
          />
        )}

        {numStraws > 0 && isQuantityMismatch && (
          <div className="validated-input-message-error">
            <AlertSVG />
            {toastMessages.ACCOUNT_STRAW_QUANTITY_MISMATCH(accountName, currentQuantity, strawTotal)}
          </div>
        )}
      </div>
    );
  };

  const specimenOptions = specimenIds.map(toSpecimenOption);

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={900}>
      <div className="add-straw-level-data-modal">
        <div className="header">
          <h4>{BUTTON_CONSTANTS.ADD_STRAWS}</h4>
        </div>

        <div className="body">
          {specimenIds.length === 0 ? (
            <p className="no-eligible-specimens">{LABEL_CONSTANTS.NO_INVENTORY}</p>
          ) : (
            <>
              <div className="specimen-field-row">
                <label htmlFor="add-straw-specimen-select">{LABEL_CONSTANTS.LOT_NO}:</label>
                <div className="input-container">
                  <FilterDropdown
                    id="add-straw-specimen-select"
                    placeholder={LABEL_CONSTANTS.SELECT_ONE}
                    options={specimenOptions}
                    value={selectedSpecimenOption}
                    onChange={handleSpecimenChange}
                    disabled={mode === AddStrawLevelDataModalMode.Specimen}
                  />
                </div>
              </div>

              {loading ? (
                <Loader addedSpace loaderSize="small" />
              ) : accountStrawStates.length > 0 ? (
                <>
                  <div className="straw-details-section">
                    <h5 className="section-heading-main">{LABEL_CONSTANTS.STRAW_DETAILS}</h5>
                    {accountStrawStates.length > 1 && (
                      <div className="info-banner">
                        <InfoSVG size="18" color="#0f64dcf5" />
                        {LABEL_CONSTANTS.ADD_STRAW_WARNING}
                      </div>
                    )}
                    {accountForms.map(({ accountState, startIndex }) => renderAccountForm(accountState, startIndex))}
                  </div>

                  {hasAnyStraws && storageLocations.length > 0 ? (
                    <div className="storage-locations-section">
                      <h5 className="section-heading-main">{LABEL_CONSTANTS.STORAGE_LOCATION}</h5>
                      <SelectStorageLocations
                        selectedStorageLocations={storageLocations}
                        setSelectedStorageLocations={setStorageLocations}
                        quantityToMatch={totalQuantity}
                        isValidationAlertActive={isValidationAlertActive}
                        strawCodeOptions={allStrawCodeOptions}
                        itemType="Embryo"
                        hideAddLocation
                        hideRemoveLocation
                        readOnlyLocationFields
                      />

                      {isValidationAlertActive && hasUnassignedStraws() && (
                        <div className="validated-input-message-error">
                          <AlertSVG />
                          {VALIDATION_ALERT_CONSTANTS.MISSING_STORAGE_LOCATION}
                        </div>
                      )}
                    </div>
                  ) : hasAnyStraws ? (
                    <p className="no-eligible-specimens">{LABEL_CONSTANTS.NO_LOCATION_FOUND}</p>
                  ) : null}
                </>
              ) : (
                <p className="no-eligible-specimens">{LABEL_CONSTANTS.NO_INVENTORY}</p>
              )}
            </>
          )}
        </div>

        <div className="footer">
          <button className="button blue inverted medium" onClick={onClose} disabled={saving}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button
            type="button"
            className="button green medium"
            disabled={!isFormValid() || saving || loading}
            onClick={handleSave}>
            {BUTTON_CONSTANTS.SAVE}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddStrawLevelDataModal;
